import {createAsyncThunk} from '@reduxjs/toolkit';
import type {GraphicsReadinessResult} from '@Components/social-media/social-media.types';
import {GRAPHICS_READINESS_MAX_WAIT_MS} from '@Components/social-media-wizard/social-media-wizard.types';
import {updateGraphicsReadiness} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {LoadingStates} from '@Utils/loading.util';

const isGraphicsReadinessResult = (value: unknown): value is GraphicsReadinessResult => {
  return typeof value === 'object' && value !== null && 'ready' in value && typeof value.ready === 'boolean';
};

const isReady = (value: unknown): boolean => {
  return isGraphicsReadinessResult(value) && value.ready;
};

/**
 * Drives the publish-step readiness gate with the standard long-poll client: one instant kickoff check, then
 * polling only if needed. The "preparing your design" state is dispatched solely from the kickoff, and only when
 * that instant check comes back not-ready — so an already-rendered design publishes with no loader flash.
 */
export const pollPublishGraphicsReadiness = createAsyncThunk(
  'socialMediaWizard/pollPublishGraphicsReadiness',
  async ({idPost}: {idPost: string}, {dispatch}): Promise<GraphicsReadinessResult | undefined> => {
    const result: unknown = await window.PMW.pollingAjaxCallAsync(
      async (): Promise<unknown> => {
        const initial: unknown = await window.PMW.readLocal('socialmedia/getPublishGraphicsReadiness', {idPost});
        if (!isReady(initial)) {
          dispatch(updateGraphicsReadiness(LoadingStates.LOADING));
        }
        return initial;
      },
      async (initial: unknown): Promise<unknown> => {
        return isReady(initial) ? initial : window.PMW.readLocal('socialmedia/awaitPublishGraphicsReadiness', {idPost});
      },
      GRAPHICS_READINESS_MAX_WAIT_MS
    );

    return isGraphicsReadinessResult(result) ? result : undefined;
  }
);
